import { isBoolean } from 'lodash-es'
import { Participant, ParticipantEvent, Room, RoomEvent, ScreenSharePresets, Track } from 'livekit-client'

import * as THREE from '../three'

import { System } from './System'

const v1 = new THREE.Vector3()
const v2 = new THREE.Vector3()
const v3 = new THREE.Vector3()
const q1 = new THREE.Quaternion()

export class MediaSystem extends System {
  constructor(client) {
    super(client)
    this.room = null
    this.status = {
      available: false,
      connected: false,
      mic: false,
      level: 'spatial',
    }
    this.defaultLevel = 'spatial'
    this.levels = {}
    this.muted = new Set()
    this.voices = new Map() // playerId -> EntityVoice
    this.entities = new Map() // playerId -> Entity
    this.pendingTracks = new Map() // playerId -> { track, participant }
  }

  async setup(data) {
    if (!data) return
    if (this.room) {
      console.error('[media] todo: destroy previous room')
    }
    this.status.available = true
    this.status.muted = data.muted.has(this.client.network.playerId)
    this.levels = data.levels
    this.muted = data.muted
    this.room = new Room({
      webAudioMix: {
        audioContext: this.client.audio.ctx,
      },
      publishDefaults: {
        // screenShareEncoding: ScreenSharePresets.h1080fps30.encoding,
        // screenShareSimulcastLayers: [ScreenSharePresets.h1080fps30],
      },
    })
    this.room.on(RoomEvent.TrackMuted, this.onTrackMuted)
    this.room.on(RoomEvent.TrackUnmuted, this.onTrackUnmuted)
    this.room.on(RoomEvent.LocalTrackPublished, this.onLocalTrackPublished)
    this.room.on(RoomEvent.LocalTrackUnpublished, this.onLocalTrackUnpublished)
    this.room.on(RoomEvent.TrackSubscribed, this.onTrackSubscribed)
    this.room.on(RoomEvent.TrackUnsubscribed, this.onTrackUnsubscribed)
    this.room.localParticipant.on(ParticipantEvent.IsSpeakingChanged, speaking => {
      const entity = this.client.entities.player
      this.emit('speaking', { playerId: entity.id, speaking })
      entity.setVoiceActive(speaking)
    })
    this.client.audio.ready(async () => {
      await this.room.connect(data.wsUrl, data.token)
      this.status.connected = true
      this.emit('status', this.status)
      this.setMicrophoneEnabled(true)
    })
  }

  registerEntity(entity) {
    const playerId = entity.id
    this.entities.set(playerId, entity)
    this.tryBind(playerId)
    return () => {
      this.entities.delete(playerId)
      this.tryUnbind(playerId)
    }
  }

  tryBind(playerId) {
    const entity = this.entities.get(playerId)
    const pending = this.pendingTracks.get(playerId)
    // need both entity and track to bind
    if (!entity || !pending) return
    // already bound
    if (this.voices.has(playerId)) return
    const { track, participant } = pending
    const level = this.levels[playerId] || this.defaultLevel
    const muted = this.muted.has(playerId)
    const voice = new EntityVoice(this.client, entity, level, muted, track, participant)
    this.voices.set(playerId, voice)
    // console.log('bound voice for', playerId)
  }

  tryUnbind(playerId) {
    const voice = this.voices.get(playerId)
    if (!voice) return
    voice.destroy()
    this.voices.delete(playerId)
    // console.log('unbound voice for', playerId)
    // note: we keep pendingTracks entry so it can rebind if entity returns
  }

  setMuted(playerId, muted) {
    if (muted && this.muted.has(playerId)) return
    if (!muted && !this.muted.has(playerId)) return
    if (muted) {
      this.muted.add(playerId)
    } else {
      this.muted.delete(playerId)
    }
    const voice = this.voices.get(playerId)
    voice?.setMuted(muted)
    this.emit('muted', { playerId, muted })
    if (playerId === this.client.network.playerId) {
      this.status.muted = muted
      this.emit('status', this.status)
    }
  }

  isMuted(playerId) {
    return this.muted.has(playerId)
  }

  setLevel(playerId, level) {
    this.levels[playerId] = level
    level = level || this.defaultLevel
    if (playerId === this.client.network.playerId) {
      if (this.status.level !== level) {
        this.status.level = level
        this.emit('status', this.status)
      }
      return
    }
    const voice = this.voices.get(playerId)
    voice?.setLevel(level) // disabled, spatial, global
  }

  lateUpdate(delta) {
    this.updateListener()
    this.voices.forEach(voice => {
      voice.lateUpdate(delta)
    })
  }

  updateListener() {
    const audio = this.client.audio
    const listener = audio.ctx.listener

    const entity = this.client.entities.player
    if (!entity) return

    // choose the transform you consider the "ears":
    const pos = v1
    const qua = q1
    entity.getVoiceTransform(pos, qua)

    // forward = -Z, up = +Y in your local space
    const forward = v2.set(0, 0, -1).applyQuaternion(qua)
    const up = v3.set(0, 1, 0).applyQuaternion(qua)

    const endTime = audio.ctx.currentTime + audio.lastDelta

    if (listener.positionX) {
      // modern Web Audio (AudioParam-based)
      listener.positionX.linearRampToValueAtTime(pos.x, endTime)
      listener.positionY.linearRampToValueAtTime(pos.y, endTime)
      listener.positionZ.linearRampToValueAtTime(pos.z, endTime)

      listener.forwardX.linearRampToValueAtTime(forward.x, endTime)
      listener.forwardY.linearRampToValueAtTime(forward.y, endTime)
      listener.forwardZ.linearRampToValueAtTime(forward.z, endTime)

      listener.upX.linearRampToValueAtTime(up.x, endTime)
      listener.upY.linearRampToValueAtTime(up.y, endTime)
      listener.upZ.linearRampToValueAtTime(up.z, endTime)
    } else {
      // legacy API
      listener.setPosition(pos.x, pos.y, pos.z)
      listener.setOrientation(forward.x, forward.y, forward.z, up.x, up.y, up.z)
    }
  }

  async setMicrophoneEnabled(value) {
    if (!this.room) {
      console.error('[livekit] setMicrophoneEnabled failed (not connected)')
      return false
    }
    value = isBoolean(value) ? value : !this.room.localParticipant.isMicrophoneEnabled
    if (this.status.mic === value) {
      return true
    }
    try {
      await this.room.localParticipant.setMicrophoneEnabled(value)
      return true
    } catch (err) {
      return false
    }
  }

  onTrackMuted = track => {
    // console.log('onTrackMuted', track)
    if (track.isLocal && track.source === 'microphone') {
      this.status.mic = false
      this.emit('status', this.status)
    }
  }

  onTrackUnmuted = track => {
    // console.log('onTrackUnmuted', track)
    if (track.isLocal && track.source === 'microphone') {
      this.status.mic = true
      this.emit('status', this.status)
    }
  }

  onLocalTrackPublished = publication => {
    // console.log('onLocalTrackPublished', publication)
    if (publication.source === 'microphone') {
      this.status.mic = true
      this.emit('status', this.status)
    }
  }

  onLocalTrackUnpublished = publication => {
    const playerId = this.client.network.playerId
    // console.log('onLocalTrackUnpublished', pub)
    if (publication.source === 'microphone') {
      this.status.mic = false
      this.emit('status', this.status)
    }
  }

  onTrackSubscribed = (track, publication, participant) => {
    // console.log('onTrackSubscribed', track, publication, participant)
    if (track.source !== 'microphone') return
    const playerId = participant.identity
    // store track info (even if entity doesn't exist yet)
    this.pendingTracks.set(playerId, { track, participant })
    // try to bind if entity already exists
    this.tryBind(playerId)
  }

  onTrackUnsubscribed = (track, publication, participant) => {
    // console.log('onTrackUnsubscribed todo')
    if (track.source !== 'microphone') return
    const playerId = participant.identity
    // remove pending track
    this.pendingTracks.delete(playerId)
    // destroy voice if bound
    const voice = this.voices.get(playerId)
    if (voice) {
      voice.destroy()
      this.voices.delete(playerId)
    }
  }

  // onTrackSubscribed = (track, publication, participant) => {
  //   // console.log('onTrackSubscribed', track, publication, participant)
  //   const playerId = participant.identity
  //   const entity = this.client.entities.get(playerId)
  //   if (!entity) return console.error('onTrackSubscribed failed: no entity')
  //   const client = this.client
  //   if (track.source === 'microphone') {
  //     const level = this.levels[playerId] || this.defaultLevel
  //     const muted = this.muted.has(playerId)
  //     const voice = new EntityVoice(client, entity, level, muted, track, participant)
  //     this.voices.set(playerId, voice)
  //   }
  // }

  // onTrackUnsubscribed = (track, publication, participant) => {
  //   // console.log('onTrackUnsubscribed todo')
  //   const playerId = participant.identity
  //   if (track.source === 'microphone') {
  //     const voice = this.voices.get(playerId)
  //     voice?.destroy()
  //     this.voices.delete(playerId)
  //   }
  // }

  destroy() {
    this.voices.forEach(voice => {
      voice.destroy()
    })
    this.voices.clear()
    this.pendingTracks.clear()
    this.entities.clear()
    if (this.room) {
      this.room.off(RoomEvent.TrackMuted, this.onTrackMuted)
      this.room.off(RoomEvent.TrackUnmuted, this.onTrackUnmuted)
      this.room.off(RoomEvent.LocalTrackPublished, this.onLocalTrackPublished)
      this.room.off(RoomEvent.LocalTrackUnpublished, this.onLocalTrackUnpublished)
      this.room.off(RoomEvent.TrackSubscribed, this.onTrackSubscribed)
      this.room.off(RoomEvent.TrackUnsubscribed, this.onTrackUnsubscribed)
      if (this.room.localParticipant) {
        this.room.localParticipant.off(ParticipantEvent.IsSpeakingChanged)
      }
      this.room?.disconnect()
    }
  }
}

class EntityVoice {
  constructor(client, entity, level, muted, track, participant) {
    this.client = client
    this.entity = entity
    this.level = level
    this.muted = muted
    this.track = track
    this.participant = participant
    this.track.setAudioContext(client.audio.ctx)
    this.root = client.audio.ctx.createGain()
    this.panner = client.audio.ctx.createPanner()
    this.panner.panningModel = 'HRTF'
    this.panner.distanceModel = 'inverse'
    this.panner.refDistance = 1
    this.panner.maxDistance = 40
    this.panner.rolloffFactor = 3
    this.panner.coneInnerAngle = 360
    this.panner.coneOuterAngle = 360
    this.panner.coneOuterGain = 0
    this.gain = client.audio.groupGains.voice
    this.root.connect(this.gain)
    this.root.connect(this.panner)
    this.panner.connect(this.gain)
    this.track.attach()
    this.apply()
    this.participant.on(ParticipantEvent.IsSpeakingChanged, this.onSpeakingChanged)
  }

  onSpeakingChanged = speaking => {
    this.client.media.emit('speaking', { playerId: this.entity.id, speaking })
    this.entity.setVoiceActive(speaking)
  }

  setMuted(muted) {
    if (this.muted === muted) return
    this.muted = muted
    this.apply()
  }

  setLevel(level) {
    if (this.level === level) return
    this.level = level
    this.apply()
  }

  apply() {
    if (this.muted) {
      this.root.gain.value = 0
      this.track.setWebAudioPlugins([this.root])
    } else if (this.level === 'disabled') {
      this.root.gain.value = 0
      this.track.setWebAudioPlugins([this.root])
    } else if (this.level === 'spatial') {
      this.root.gain.value = 1
      this.track.setWebAudioPlugins([this.panner])
    } else if (this.level === 'global') {
      this.root.gain.value = 1
      this.track.setWebAudioPlugins([this.root])
    }
  }

  lateUpdate(delta) {
    if (this.muted) return
    if (this.level !== 'spatial') return
    const audio = this.client.audio
    const entity = this.entity
    let pos = v1
    let qua = q1
    entity.getVoiceTransform(pos, qua)
    const dir = v2.set(0, 0, -1).applyQuaternion(qua)
    if (this.panner.positionX) {
      const endTime = audio.ctx.currentTime + audio.lastDelta
      this.panner.positionX.linearRampToValueAtTime(pos.x, endTime)
      this.panner.positionY.linearRampToValueAtTime(pos.y, endTime)
      this.panner.positionZ.linearRampToValueAtTime(pos.z, endTime)
      this.panner.orientationX.linearRampToValueAtTime(dir.x, endTime)
      this.panner.orientationY.linearRampToValueAtTime(dir.y, endTime)
      this.panner.orientationZ.linearRampToValueAtTime(dir.z, endTime)
    } else {
      this.panner.setPosition(pos.x, pos.y, pos.z)
      this.panner.setOrientation(dir.x, dir.y, dir.z)
    }
  }

  destroy() {
    this.participant.off(ParticipantEvent.IsSpeakingChanged, this.onSpeakingChanged)
    this.client.media.emit('speaking', { playerId: this.entity.id, speaking: false })
    this.entity.setVoiceActive(false)
    this.track.detach()
  }
}
